import React, { useEffect, useRef, useState } from 'react';
import { Tab, Tabs } from '@material-ui/core';
import CustomImageViewer from '../custom-image-viewer/Custom-Image-Viewer';
import { ASSET_URL } from '../../env/Configurations';
import './MediaViewer.scss';
import RedirectUrlLink from '../redirect-url-link/Redirect-Url-Link';

const GifStill = ({ src, alt, className, style }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      let { width, height } = c.getBoundingClientRect();
      if (!width || !height) {
        width = 120;
        height = 120;
        c.style.width = `${width}px`;
        c.style.height = `${height}px`;
      }
      c.width = Math.max(1, Math.round(width));
      c.height = Math.max(1, Math.round(height));
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
    };
  }, [src]);

  return (
    <canvas
      ref={canvasRef}
      role='img'
      aria-label={alt || 'GIF preview'}
      className={className}
      style={style}
    />
  );
};

const assetUrl = (raw) => {
  if (!raw) return '';
  if (/^(https?:|blob:)/i.test(raw)) return raw;
  return `${ASSET_URL}${raw}`;
};

const MediaViewer = ({
  images = [],
  videos = {},
  gifs = {},
  enabledMediaTabs = ['image', 'video', 'gif']
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const videoClipsRaw = videos?.clips || videos?.files || [];
  const videoClips = (videoClipsRaw || []).map((v, idx) => {
    const url = v?.videoUrl ?? v?.mediaUrl ?? '';
    return {
      id: v?.id || v?.videoId || v?.fileName || `video_${idx}`,
      url: assetUrl(url),
      uploadedAt: v?.uploadDate,
    };
  });

  const gifItemsRaw = gifs?.items || gifs?.files || [];
  const gifItems = (gifItemsRaw || []).map((g, idx) => {
    const raw = g?.gifUrl ?? g?.mediaUrl ?? g?.url ?? '';
    return {
      id: g?.id || g?.fileName || `gif_${idx}`,
      url: assetUrl(raw),
      uploadedAt: g?.uploadDate,
      size: g?.fileSize ?? g?.size,
    };
  });

  const renderImagesTab = () => (
    <div className='row pt-2'>
      <CustomImageViewer images={images || []} />
    </div>
  );

  const renderVideosTab = () => (
    <div className='row pt-2 px-3'>
      <h4 className='custom-image-viewer-title '>
        {videoClips?.length || 0} {videoClips?.length === 1 ? 'Video' : 'Videos'}
      </h4>
      {videoClips?.length ? (
        <div className='video-list-container'>
          {videoClips.map((video, index) => (
            <div key={video.id || index} className='video-item'>
               <RedirectUrlLink url={video.url} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  const renderGifsTab = () => (
    <div className='row pt-2 px-2'>
      <h4 className='custom-image-viewer-title px-4'>
        {gifItems?.length || 0} {gifItems?.length === 1 ? 'GIF' : 'GIFs'}
      </h4>
      {gifItems?.length ? (
        <div className='d-flex custom-image-viewer-gallery'>
          {gifItems.map((gif, index) => {
            const raw = gif.gifUrl ?? gif.url ?? '';
            const gifUrl = raw ? (raw.startsWith('http') ? raw : `${ASSET_URL}${raw}`) : '';
            return (
              <div key={gif.id || index} className='pt-2 image-custom'>
                <GifStill
                  src={gifUrl}
                  alt={`GIF ${index + 1}`}
                  className='custom-image-viewer-card'
                  style={{ display: 'block', width: 120, height: 120 }}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  const tabIndexMap = {
    image: 0,
    video: 1,
    gif: 2
  };

  return (
    <div className='media-viewer-container'>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor='primary'
        textColor='primary'
        className='media-tabs'
      >
        {enabledMediaTabs.includes('image') && <Tab label='Images' className='media-tab' />}
        {enabledMediaTabs.includes('video') && <Tab label='Videos' className='media-tab' />}
        {enabledMediaTabs.includes('gif') && <Tab label='GIFs' className='media-tab' />}
      </Tabs>

      <div className='media-tab-content px-2'>
        {activeTab === 0 && enabledMediaTabs[0] === 'image' && renderImagesTab()}
        {activeTab === 0 && enabledMediaTabs[0] === 'video' && renderVideosTab()}
        {activeTab === 0 && enabledMediaTabs[0] === 'gif' && renderGifsTab()}
        {activeTab === 1 && enabledMediaTabs[1] === 'image' && renderImagesTab()}
        {activeTab === 1 && enabledMediaTabs[1] === 'video' && renderVideosTab()}
        {activeTab === 1 && enabledMediaTabs[1] === 'gif' && renderGifsTab()}
        {activeTab === 2 && enabledMediaTabs[2] === 'image' && renderImagesTab()}
        {activeTab === 2 && enabledMediaTabs[2] === 'video' && renderVideosTab()}
        {activeTab === 2 && enabledMediaTabs[2] === 'gif' && renderGifsTab()}
      </div>
    </div>
  );
};

export default MediaViewer;
